import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DatePicker, Button } from 'antd-mobile';
import './create.scss';
// 导入所需工具函数
import request from '@/utils/request';
import { createNonceStr, md5, getLocationSearch } from "@/utils/utils";

// 自定义 Toast 组件
const Toast = {
  show: ({ content, duration = 2000 }) => {
    const toast = document.createElement('div');
    toast.className = 'custom-toast';
    toast.innerText = content;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('show');
    }, 10);
    
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, duration);
  }
};

// 地址搜索组件
const PlaceSearch = ({ placeholder, value, onChange, label }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const API_KEY = 'OXTBZ-JFXRL-PLQPT-MGZTX-ITW7Z-IQFJQ';

  useEffect(() => {
    // 点击外部时关闭建议列表
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // JSONP请求辅助函数
  const jsonpRequest = (url, callback) => {
    const callbackName = 'jsonp_callback_' + Math.round(100000 * Math.random());
    window[callbackName] = (data) => {
      delete window[callbackName];
      document.body.removeChild(script);
      console.log('JSONP callback:', data);
      callback(data);
    };
    
    const script = document.createElement('script');
    url += (url.indexOf('?') === -1 ? '?' : '&') + 'callback=' + callbackName;
    script.src = url;
    document.body.appendChild(script);
  };

  const searchPlaces = async (keyword) => {
    if (!keyword) return;
    
    setIsSearching(true);
    
    try {
      // 使用JSONP方式请求腾讯地图API
      const url = `https://apis.map.qq.com/ws/place/v1/suggestion?key=${API_KEY}&keyword=${encodeURIComponent(keyword)}&output=jsonp`;
      // 处理JSONP请求
      
      jsonpRequest(url, (result) => {
        setIsSearching(false);
        
        if (result && result.status === 0 && result.data && result.data.length > 0) {
          setSuggestions(result.data);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          if (result.status !== 0) {
            Toast.show({ content: `搜索失败：${result.message}`, duration: 2000 });
          }
        }
      });
    } catch (error) {
      setIsSearching(false);
      setSuggestions([]);
      Toast.show({ content: '搜索失败，请重试', duration: 2000 });
      console.error('地址搜索错误:', error);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setKeyword(value);
    
    // 当输入超过2个字符时开始搜索
    if (value.length >= 2) {
      searchPlaces(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange(suggestion.title);
    setKeyword(suggestion.title);
    setShowSuggestions(false);
  };

  return (
    <div className="place-search-container">
      <label className="form-label">{label}</label>
      <div className="search-input-wrapper">
        <input
          ref={inputRef}
          className="form-input"
          type="text"
          placeholder={placeholder}
          value={keyword}
          onChange={handleInputChange}
          onFocus={() => keyword.length >= 2 && setShowSuggestions(true)}
          required
        />
        {isSearching && <div className="search-loading"></div>}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <ul className="place-suggestions" ref={suggestionsRef}>
          {suggestions.map((suggestion, index) => (
            <li 
              key={index} 
              onClick={() => handleSelectSuggestion(suggestion)}
              className="suggestion-item"
            >
              <span className="suggestion-name">{suggestion.title}</span>
              <span className="suggestion-address">{suggestion.address}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// 自定义时间格式化函数
const formatDateTime = (date) => {
  if (!date) return '';
  
  try {
    const pad = (num) => (num < 10 ? `0${num}` : num);
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (e) {
    console.error('日期格式化错误:', e);
    return '';
  }
};

// 安全的日期解析函数，兼容iOS
const parseDateTime = (dateString) => {
  if (!dateString) return new Date();
  
  // 如果已经是Date对象则直接返回
  if (dateString instanceof Date) return dateString;
  
  try {
    // 将日期时间字符串转换为ISO兼容格式 (将空格替换为T，确保兼容iOS)
    if (typeof dateString === 'string') {
      dateString = dateString.replace(' ', 'T');
      // 如果没有时区信息，添加本地时区信息
      if (dateString.indexOf('Z') === -1 && dateString.indexOf('+') === -1) {
        dateString = dateString + 'Z';
      }
    }
    
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? new Date() : date;
  } catch (e) {
    console.error('日期解析错误:', e);
    return new Date();
  }
};

const CreateOrder = () => {
  const [formData, setFormData] = useState({
    driverId: '',
    fromAddress: '',
    startTime: '',
    toAddress: '',
    totalFee: '',
  });
  const [loading, setLoading] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);

  // 构建请求参数
  const getBaseParams = () => {
    let token = getLocationSearch().token;
    let appKey = "app_h5";
    let secret = "11dc72cf-88b0-46cd-91d7-b1a77fd69ee1";
    let timestamp = new Date().getTime();
    let random = createNonceStr();
    let tempStr = `appKey${appKey}secret${secret}timestamp${timestamp}random${random}token${token}`;
    let sign = md5(tempStr, 32).toString(16).toLocaleUpperCase();
    
    return {
      appKey: appKey,
      timestamp: timestamp,
      random: random,
      token: token,
      sign: sign,
    };
  };

  // 获取司机信息
  const fetchDriverInfo = async () => {
    try {
      const params = getBaseParams();
      const response = await request.get('/api/driverUser/checkToken', { token: params.token }, params);
      
      if (response.data && response.data.errorCode === 0) {
        const driverData = response.data.data;
        setDriverInfo(driverData);
        // 设置司机ID到表单中
        setFormData(prev => ({
          ...prev,
          driverId: driverData.id
        }));
      } else {
        Toast.show({ content: `获取司机信息失败: ${response.data?.errorMsg || '未知错误'}`, duration: 2000 });
      }
    } catch (error) {
      console.error('获取司机信息失败:', error);
      Toast.show({ content: '获取司机信息失败，请重试', duration: 2000 });
    }
  };

  // 组件挂载时获取司机信息
  useEffect(() => {
    fetchDriverInfo();
  }, []);

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.driverId) {
      Toast.show({ content: '未能获取司机信息，请刷新页面重试', duration: 2000 });
      return;
    }
    
    setLoading(true);
    try {
      const params = getBaseParams();
      // 确保日期格式正确
      const submitData = {
        ...formData,
        startTime: formData.startTime // 已经是格式化的字符串，不需要额外处理
      };
      
      const response = await request.post('/api/offlineOrder/create', submitData, params);
      
      if (response.data && response.data.errorCode === 0) {
        Toast.show({ content: '订单创建成功！', duration: 2000 });
        setTimeout(() => {
          window.history.back();
        }, 1500); 
      } else {
        console.error('创建订单失败:', response.data?.errorMsg || '未知错误');
        Toast.show({ content: `错误：${response.data?.errorMsg || '未知错误'}`, duration: 2000 });
      }
    } catch (error) {
      Toast.show({ content: '请求失败，请稍后重试。', duration: 2000 });
      console.error('创建订单错误:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-order">
      <form onSubmit={handleSubmit}>
        <div className="form-item">
          <PlaceSearch 
            label="起点地址" 
            placeholder="请搜索起点地址" 
            value={formData.fromAddress} 
            onChange={(value) => handleChange('fromAddress', value)} 
          />
        </div>
        <div className="form-item">
          <PlaceSearch 
            label="终点地址" 
            placeholder="请搜索终点地址" 
            value={formData.toAddress} 
            onChange={(value) => handleChange('toAddress', value)} 
          />
        </div>
        <div className="form-item">
          <label className="form-label">总金额</label>
          <input
            className="form-input"
            type="text" // 改为text类型，避免iOS上number类型的问题
            inputMode="decimal" // 提示浏览器显示数字键盘，支持小数点
            placeholder="请输入总金额"
            value={formData.totalFee}
            onChange={(e) => {
              // 只允许输入数字和小数点
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                handleChange('totalFee', value);
              }
            }}
            onBlur={(e) => {
              // 失去焦点时格式化金额，确保有效的金额格式
              let value = e.target.value;
              if (value && !isNaN(parseFloat(value))) {
                // 转换为两位小数的金额
                value = parseFloat(value).toFixed(2);
                handleChange('totalFee', value);
              }
            }}
            required
          />
        </div>
        <div className="form-item">
          <label className="form-label">开始时间</label>
          <div className="date-picker-container">
            <div 
              className="form-input date-picker-trigger"
              onClick={() => setDatePickerVisible(true)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              {formData.startTime ? formData.startTime : '请选择开始时间'}
            </div>
            <DatePicker
              visible={datePickerVisible}
              value={formData.startTime ? parseDateTime(formData.startTime) : new Date()}
              precision="minute"
              onDismiss={() => setDatePickerVisible(false)}
              onOk={(val) => {
                handleChange('startTime', formatDateTime(val));
                setDatePickerVisible(false);
              }}
            />
          </div>
        </div>
        <button
          className="submit-button"
          type="submit"
          disabled={loading || !formData.driverId}
        >
          {loading ? '处理中...' : '提交订单'}
        </button>
      </form>
    </div>
  );
};

export default CreateOrder;
